<template>
  <div class="hero-card w-full relative" :class="heroHeight" :data-hero-family="family" :data-text-size="header?.textSize || 'medium'">
    <video v-if="selectedMedia === 'video' && (mobileVideo || desktopVideo)"
           autoplay
           muted="true"
           playsinline
           loop
           class="absolute w-full h-full object-cover z-20 motion-reduce:hidden!"
           aria-hidden="true">
      <!--      These won't switch src like image when resizing the window, which is just due to how browsers work.
          This likely won't be an issue for 99.9% of users   -->
      <!--      If they only provided one of either a mobile or desktop video, use the other as the fallback-->
      <source :media="'(max-width:' + (mobileMediaBreakpoint === 'tablet' ? '1280px' : '768px') + ')'"
        :src="mobileVideo || desktopVideo">
      <source :src="desktopVideo || mobileVideo">
    </video>
    <picture v-else-if="selectedMedia === 'image' && (mobileImage || desktopImage)">
      <!--      Unlike video, these will switch src dynamically when the viewport resizes (someone changes window size) -->
      <!--      If they only provided one of either a mobile or desktop image, use the other as the fallback -->
      <source :media="'(max-width:' + (mobileMediaBreakpoint === 'tablet' ? '1280px' : '768px') + ')'"
        :srcset="mobileImage || desktopImage">
      <img class="absolute w-full h-full object-cover z-10" :src="desktopImage || mobileImage"
        :alt="imageDecorative ? '' : imageAlt" :aria-hidden="imageDecorative ? 'true' : undefined" />
    </picture>
    <div v-if="header?.line1"
         class="hero-content relative w-full flex flex-col leading-110 py-8 bg-linear-to-br from-10% from-black/0 to-100% z-30"
         :class="[heroHeight, headerPosition]"
         :style="[getTextColorStyling(heroTextColor), getGradientStyling(tintColor)]">
      <div class="hero-copy w-fit max-w-full flex flex-col gap-6 sm:gap-8 px-8 sm:px-16" :class="internalAlignment">
        <nav v-if="family === 'fullWidthBreadcrumbs' && validBreadcrumbs.length" aria-label="Breadcrumb">
          <ol class="flex flex-wrap gap-2">
            <li v-for="(crumb, index) in validBreadcrumbs" :key="index">
              <span v-if="index" aria-hidden="true"> / </span>
              <span v-if="index === validBreadcrumbs.length - 1" aria-current="page">{{ crumb.label }}</span>
              <a v-else :href="crumb.link" class="underline focus-visible:outline-2 focus-visible:outline-offset-4">{{ crumb.label }}</a>
            </li>
          </ol>
        </nav>
        <span v-if="header?.topSubtext?.trim()" class="hero-eyebrow text-xl sm:text-4xl font-medium mb-1"
          :style="getTextColorStyling(heroTextColor)">{{header.topSubtext}}</span>
        <h1 ref="primaryHeading" class="hero-heading font-medium" :class="headerTextSize">{{header?.line1}}</h1>
        <h2 ref="secondaryHeading" class="hero-heading font-medium" :class="headerTextSize" v-if="header?.line2?.trim()">{{header?.line2}}</h2>
        <p v-if="editing && (headingTooLong || headingExceedsLines)" role="status">Heading exceeds the recommended 40 characters or two lines. Check the heading at each screen size.</p>
        <p v-if="editing && selectedMedia === 'image' && (mobileImage || desktopImage) && !imageDecorative && !imageAlt.trim()" role="status">Add alt text for this informative image, or mark it decorative if it conveys no information.</p>
        <span v-if="header?.bottomSubtext?.trim()" class="hero-subheading text-xl sm:text-4xl mb-1 font-medium"
          :style="getTextColorStyling(heroTextColor)">{{header.bottomSubtext}}</span>
        <div v-if="validButtons.length"
          class="hero-actions flex flex-row gap-4 w-full" :class="buttonAlignment">
          <div v-for="(button, index) in validButtons" :key="index">
            <a
              class="hero-action"
              :href="button.link"
              :class="[
                'font-bold text-[1.143rem] py-[0.714rem] px-[1.429rem] focus-visible:outline-2 focus-visible:outline-offset-4',
                getTheme(button.buttonTheme)
              ]"
            >
              <span v-if="button.leftIcon?.trim()" class="hero-action-icon" aria-hidden="true">
                <Icon pack-prefix="far" :icon="button.leftIcon.trim()" />
              </span>
              <span class="hero-action-label">{{ button.text }}</span>
              <span v-if="button.rightIcon?.trim()" class="hero-action-icon" aria-hidden="true">
                <Icon pack-prefix="far" :icon="button.rightIcon.trim()" />
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isEditing } from '@builder.io/sdk-vue'
import Icon from '~/components/ui/atom/icon.vue'

interface ActionButton {
    text: string,
    link: string,
    buttonTheme: "DARK" | "LIGHT";
    leftIcon?: string;
    rightIcon?: string;
}

export interface Header {
  line1: string,
  line2: string,
  textColor: string,
  textSize: string,
  horizontalPosition: string,
  verticalPosition: string
  topSubtext: string,
  bottomSubtext: string,
  buttons: ActionButton[]
}

interface HeroCardProps {
  horizontalPosition?: 'left' | 'center'
  verticalPosition?: 'center' | 'bottom'
  background?: 'dark' | 'light'
  family?: 'main' | 'medium' | 'short' | 'fullWidthBreadcrumbs'
  mediaType?: 'image' | 'video'
  imageAlt?: string
  imageDecorative?: boolean
  breadcrumbs?: { label: string; link?: string }[]
  desktopVideo: string
  mobileVideo: string
  desktopImage: string
  mobileImage: string
  // Retain saved legacy selections; new entries use the component's mobile breakpoint.
  mobileMediaBreakpoint?: string
  header: Header
  tintColor?: string
}

const {
  family = 'main',
  horizontalPosition: authorHorizontalPosition,
  verticalPosition: authorVerticalPosition,
  background,
  mediaType,
  imageAlt = '',
  imageDecorative = true,
  breadcrumbs = [],
  desktopVideo,
    mobileVideo,
    desktopImage,
    mobileImage,
    mobileMediaBreakpoint = 'mobile',
    header,
    tintColor = '#000000',
} = defineProps<HeroCardProps>()

const editing = ref(false)
const primaryHeading = ref<HTMLElement | null>(null)
const secondaryHeading = ref<HTMLElement | null>(null)
const headingExceedsLines = ref(false)
let headingObserver: ResizeObserver | undefined
const measureHeading = () => {
  const lines = [primaryHeading.value, secondaryHeading.value].reduce((total, element) => {
    if (!element) return total
    const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight)
    return total + (lineHeight > 0 ? Math.round(element.getBoundingClientRect().height / lineHeight) : 0)
  }, 0)
  headingExceedsLines.value = lines > 2
}
const observeHeading = async () => {
  await nextTick()
  headingObserver?.disconnect()
  for (const element of [primaryHeading.value, secondaryHeading.value]) {
    if (element) headingObserver?.observe(element)
  }
  measureHeading()
}
onMounted(() => {
  editing.value = isEditing()
  if (!editing.value) return
  if (typeof ResizeObserver !== 'undefined') headingObserver = new ResizeObserver(measureHeading)
  void observeHeading()
})
watch(() => [header?.line1, header?.line2, header?.textSize, family], () => {
  if (editing.value) void observeHeading()
})
onBeforeUnmount(() => headingObserver?.disconnect())
// Old entries have no media selector. Retain their video-first presentation.
const selectedMedia = computed(() => mediaType || (mobileVideo || desktopVideo ? 'video' : 'image'))
const validButtons = computed(() => (header?.buttons || [])
  .filter(button => button.text?.trim() && button.link?.trim()).slice(0, 2))
const validBreadcrumbs = computed(() => breadcrumbs.filter((crumb, index) =>
  crumb.label?.trim() && (index === breadcrumbs.length - 1 || crumb.link?.trim())))
const headingTooLong = computed(() => [header?.line1, header?.line2].filter(Boolean).join(' ').length > 40)
// Use the Main design's white foreground only when no author color is selected.
const heroTextColor = computed(() => {
  if (header?.textColor?.trim()) return header.textColor
  if (background === 'dark') return '#ffffff'
  if (background === 'light') return '#000000'
  return family === 'main' ? '#ffffff' : 'inherit'
})

// Main frame heights from Figma, using the site's existing tablet/desktop breakpoints.
// Minimum heights let content grow when text is enlarged instead of clipping it.
const heroHeight = computed(() => family === 'main'
  ? 'min-h-[480px] sm:min-h-[540px] xl:min-h-[732px]'
  : 'min-h-[372px] xl:min-h-[720px]')

const headerTextSize = computed(() => {
    return {
        'text-xl lg:text-5xl': header?.textSize === 'small',
        'text-2xl lg:text-6xl': header?.textSize === 'medium',
        'text-3xl lg:text-7xl': header?.textSize === 'large',
        'text-4xl lg:text-8xl': header?.textSize === 'XL',
        'text-5xl lg:text-9xl': header?.textSize === '2XL'
    }
})

const horizontalPosition = computed(() => {
  if (family === 'short') return 'center'
  if (family === 'medium' || family === 'fullWidthBreadcrumbs') return 'left'
  return (authorHorizontalPosition || header?.horizontalPosition) === 'center' ? 'center' : 'left'
})

const verticalPosition = computed(() => {
  if (family === 'short' || family === 'fullWidthBreadcrumbs') return 'center'
  const selected = authorVerticalPosition || header?.verticalPosition
  return selected === 'bottom' || (!selected && family === 'main') ? 'bottom' : 'center'
})

const headerPosition = computed(() => {
    return {
        'justify-center': verticalPosition.value === 'center',
        'justify-end': verticalPosition.value === 'bottom',

        'items-center text-center': horizontalPosition.value === 'center',
        'items-start text-left': horizontalPosition.value === 'left'
    }
})

const internalAlignment = computed(() => {
    return {
        'items-center': horizontalPosition.value === 'center',
        'items-start': horizontalPosition.value === 'left'
    }
})

const buttonAlignment = computed(() => {
  return {
    'items-center justify-center': horizontalPosition.value === 'center',
    'items-start justify-start': horizontalPosition.value === 'left'
  }
})

// can't use dynamic classes with tailwind, have to use styling
const getTextColorStyling = (color: string) => {
  return {color: color || 'none'}
}

const getGradientStyling = (color: string) => {
  return [
    "--tw-gradient-from: "+color+" var(--tw-gradient-from-position);"
  ]
}

const getTheme = (theme: string) => {
  return theme === 'DARK' ? 'hero-action--on-dark' : 'hero-action--on-light'
}

</script>

<style scoped>
.hero-actions {
  flex-wrap: nowrap;
}

.hero-actions > div {
  min-width: 0;
}

.hero-action {
  max-width: 100%;
  gap: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 44px;
}

.hero-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  line-height: 1;
  flex-shrink: 0;
}

.hero-action-icon :deep(svg) {
  display: block;
  height: 16px;
  width: auto;
  vertical-align: 0;
}

.hero-action-label {
  min-width: 0;
  overflow-wrap: anywhere;
  line-height: 1.2;
}

.hero-action.hero-action--on-dark {
  color: #222;
  background-color: #fff;
}

.hero-action.hero-action--on-dark:hover {
  color: #222;
  background-color: #eaeaea;
}

.hero-action.hero-action--on-light {
  color: #fff;
  background-color: #222;
}

.hero-action.hero-action--on-light:hover {
  color: #fff;
  background-color: #555;
}

.hero-action:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
  box-shadow: 0 0 0 4px #222;
}

  .hero-card[data-hero-family='main'] {
    container-name: main-hero;
    container-type: inline-size;
    min-height: 480px;
    background-color: #000;
    color: #fff;
  }

  .hero-card[data-hero-family='main'] .hero-content {
    padding: clamp(32px, 4.92cqw, 64px) clamp(24px, 3.69cqw, 48px);
  }

  .hero-card[data-hero-family='main'] .hero-copy {
    gap: 16px;
    min-width: 0;
    padding: 0 0 clamp(16px, 3.69cqw, 48px);
  }

  .hero-card[data-hero-family='main'] .hero-heading {
    width: 646px;
    max-width: 100%;
    margin: 0;
    line-height: 1.05;
    font-weight: 400;
    letter-spacing: -0.32px;
    overflow-wrap: anywhere;
  }

  .hero-card[data-hero-family='main'] .hero-subheading,
  .hero-card[data-hero-family='main'] .hero-eyebrow {
    margin: 0;
    font-size: clamp(16px, 1.54cqw, 20px);
    line-height: 1.4;
    font-weight: 400;
  }

  .hero-card[data-hero-family='medium'] .hero-actions {
    flex-direction: row;
    flex-wrap: nowrap;
  }

  .hero-card[data-hero-family='main'] .hero-actions {
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 8px;
    margin-top: 16px;
  }

  .hero-card[data-hero-family='main'] .hero-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 10px 20px;
    border: 1px solid currentColor;
    font-size: 14px;
    line-height: 1.4;
    font-weight: 400;
  }

  @media (width <= 640px) {
    .hero-card[data-hero-family='main'] .hero-copy {
      width: 287px;
      max-width: 100%;
      min-width: 0;
    }

    .hero-card[data-hero-family='main'] .hero-heading {
      width: 100%;
    }
  }

  @media (width > 640px) and (width < 1280px) {
    .hero-card[data-hero-family='main'] .hero-content {
      min-height: 540px;
      padding: 40px 32px;
    }

    .hero-card[data-hero-family='main'] .hero-copy {
      width: 100%;
      max-width: 646px;
      padding: 0;
    }

    .hero-card[data-hero-family='main'] .hero-heading {
      width: 100%;
      line-height: 1.15;
    }

  }
  @media (width > 640px) and (width < 992px) {
    .hero-card[data-hero-family='medium'] .hero-heading {
      width: 407px;
      max-width: 100%;
    }
  }

  @media (min-width: 992px) {
    .hero-card[data-hero-family='medium'] {
      max-width: 1302px;
      min-height: 480px;
      margin-inline: auto;
      background-color: #000;
    }

    .hero-card[data-hero-family='medium'] .hero-content {
      min-height: 480px;
      padding-inline: 64px;
    }

    .hero-card[data-hero-family='medium'] .hero-heading {
      width: 729px !important;
      max-width: 100%;
      min-width: 0;
      padding-inline: 0;
    }

    .hero-card[data-hero-family='medium'] .hero-copy {
      width: 729px;
      max-width: 100%;
      min-width: 0;
      padding-inline: 0;
    }
  }
  @media (min-width: 992px) {
    .hero-card[data-hero-family='short'] {
      max-width: 1302px;
      min-height: 320px;
    }

    .hero-card[data-hero-family='short'] .hero-content {
      min-height: 320px;
    }
  }
  @media (min-width: 992px) {
    .hero-card[data-hero-family='fullWidthBreadcrumbs'] {
      max-width: 1440px;
      min-height: 286px;
      margin-inline: auto;
    }

    .hero-card[data-hero-family='fullWidthBreadcrumbs'] .hero-content {
      min-height: 286px;
    }
  }
</style>
